import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Rocket, PlayCircle } from 'lucide-react';
import { Card, Button, Pill, PhaseGate } from '../';
import SkillPromotionCard from '../skill/SkillPromotionCard';
import SkillEvalPanel from '../skill/SkillEvalPanel';
import { useToast } from '../../../contexts/ToastContext';
import * as api from '../../../services/api';

/** Map the autonomy vocab to a human label (FULL/SELF_AUTONOMOUS → Autonomous). */
function autonomyLabel(a) {
  switch ((a || '').toUpperCase()) {
    case 'FULL': case 'SELF_AUTONOMOUS': return 'Autonomous';
    case 'SUPERVISED': return 'Supervised';
    case 'RESTRICTED': return 'Restricted';
    case 'PROBATION': case 'BLOCKED': return 'Blocked';
    default: return a || 'Not set';
  }
}

/**
 * ExteletActTab — Prove 6 (Execute) for external agents.
 *
 * Replaces the native publish-oriented ActTab for ExteLets. Per
 * EXTELET_7PROVE_JOURNEY §6/§7 (D-2): the CTA is **Mark Execution Ready**, not
 * "Publish to Agentlet". Surfaces:
 *   1. Execution-Ready status banner.
 *   2. The Execution-Ready gate (all §7 checks) — the SAME checklist
 *      ExternalAgentLifecycleService.markExecutionReady enforces (displayed ==
 *      enforced). The gateway only admits production governed traffic once ready.
 *   3. Run live probe + recent probe results (the "≥1 green probe" signal).
 *
 * NOTE (Prove7-journey redesign / J1b): access grants (the "assignment present"
 * signal) moved OUT of this surface into the dedicated RBAC journey step
 * (ExteletAccessPanel). The assignment_present gate check remains here,
 * server-evaluated, and reflects grants made on the RBAC step.
 *
 * @param {Object} props
 * @param {Object} props.agent
 * @param {Object} props.registration
 * @param {Function} props.reloadRegistration
 * @param {Function} [props.reloadAgentFromParent]
 */
export default function ExteletActTab({ agent, registration, reloadRegistration, reloadAgentFromParent }) {
  const { id } = useParams();
  const agentId = id || agent?.id;
  const registrationId = registration?.id;
  const executionReady = !!agent?.executionReadyAt;

  const toastCtx = useToast();
  const addToast = useCallback((arg) => {
    if (toastCtx && typeof toastCtx.addToast === 'function') return toastCtx.addToast(arg);
    // eslint-disable-next-line no-console
    console.warn('[ExteletActTab] toast unavailable, dropped:', arg);
    return null;
  }, [toastCtx]);

  // ── Readiness gate ────────────────────────────────────────────────
  const [gateChecks, setGateChecks] = useState([]);
  const [gateLoading, setGateLoading] = useState(true);
  const loadGate = useCallback(async () => {
    if (!registrationId) { setGateLoading(false); return; }
    setGateLoading(true);
    try {
      const res = await api.gelGetExecutionReadiness(registrationId);
      const checks = Array.isArray(res?.data?.checks) ? res.data.checks : [];
      setGateChecks(checks);
    } catch (e) {
      setGateChecks([{ check: 'preview_error', passed: false,
        message: e?.message || 'Failed to load the Execution-Ready gate' }]);
    } finally {
      setGateLoading(false);
    }
  }, [registrationId]);

  // ── Live probe ────────────────────────────────────────────────────
  const [probing, setProbing] = useState(false);
  const runProbe = useCallback(async () => {
    if (!registrationId) return;
    setProbing(true);
    try {
      const res = await api.gelRunIntegrationProbe(registrationId);
      if (res && res.ok === false) throw new Error(res.error || 'Probe failed');
      addToast({ tone: 'success', title: 'Live probe recorded', description: 'Probe result added to the agent.' });
      if (typeof reloadRegistration === 'function') await reloadRegistration();
      await loadGate();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Probe failed', description: e?.message || String(e) });
    } finally {
      setProbing(false);
    }
  }, [registrationId, addToast, reloadRegistration, loadGate]);

  const probeResults = Array.isArray(registration?.integrationTestResults)
    ? registration.integrationTestResults : [];

  /* Access grants (the "assignment present" signal) moved to the dedicated
   * RBAC journey step (ExteletAccessPanel) in the Prove7-journey redesign.
   * The assignment_present check below is still server-evaluated and refreshes
   * whenever this gate reloads (mount / probe / mark). */

  // ── Mark Execution Ready ──────────────────────────────────────────
  const [marking, setMarking] = useState(false);
  const markReady = useCallback(async () => {
    if (!registrationId) return;
    setMarking(true);
    try {
      const res = await api.gelMarkExecutionReady(registrationId);
      if (res && res.ok === false) throw new Error(res.error || res.data?.error || 'Mark Execution Ready failed');
      addToast({ tone: 'success', title: 'Execution Ready', description: 'Readiness attestation sealed. The gateway now admits production traffic.' });
      if (typeof reloadRegistration === 'function') await reloadRegistration();
      if (typeof reloadAgentFromParent === 'function') await reloadAgentFromParent();
      await loadGate();
    } catch (e) {
      addToast({ tone: 'danger', title: 'Not yet Execution Ready', description: e?.message || String(e) });
    } finally {
      setMarking(false);
    }
  }, [registrationId, addToast, reloadRegistration, reloadAgentFromParent, loadGate]);

  useEffect(() => { loadGate(); }, [loadGate]);

  // Read-only mirror of the Decide-phase settable attributes + policy skills so
  // it's unambiguous WHAT is being Marked Execution Ready (Bug D1/D2 — settable
  // sections must stay visible in Act; edit happens via Promote → Decide).
  const tenantId = agent?.tenantId;
  const [actSkills, setActSkills] = useState([]);
  const [autonomyLevel, setAutonomyLevel] = useState(null);
  const [baselineReg, setBaselineReg] = useState(false);
  const loadExecConfig = useCallback(async () => {
    if (!agentId) return;
    try {
      const sk = await api.getAgentSkills?.(agentId);
      const list = Array.isArray(sk?.data) ? sk.data : (Array.isArray(sk) ? sk : []);
      setActSkills(list.filter((s) => s.skillType === 'policy_skill'));
    } catch (e) { /* non-fatal */ }
    try {
      const p = await api.getTrustProfile(agentId);
      setAutonomyLevel(p?.data?.currentAutonomyLevel ?? null);
    } catch (e) { /* non-fatal */ }
    try {
      if (tenantId) {
        const c = await api.getTrustScoringConfig(tenantId, 'AGENT', agentId);
        const cfg = c?.data ?? null;
        setBaselineReg(!!(cfg && cfg.requiredConformance != null));
      }
    } catch (e) { /* non-fatal */ }
  }, [agentId, tenantId]);
  useEffect(() => { loadExecConfig(); }, [loadExecConfig]);

  // ── Derived ───────────────────────────────────────────────────────
  const GATE_LABELS = {
    registration_exists: 'Registration on file',
    phase_act: 'Advanced to Act',
    identity_valid: 'Identity valid',
    intent_bound: 'Intent bound',
    governance_materialized: 'Governance materialized',
    green_probe: '≥1 green probe',
    kill_switch_ready: 'Kill switch armed',
    assignment_present: 'Assignment present',
    preview_error: 'Gate preview',
  };
  const criteria = gateChecks.map((c) => ({
    id: c.check,
    label: GATE_LABELS[c.check] || c.check,
    passed: !!c.passed,
    detail: c.message,
  }));
  const allGreen = !gateLoading && criteria.length > 0 && criteria.every((c) => c.passed);
  const passedCount = criteria.filter((c) => c.passed).length;

  return (
    <div className="space-y-5">
      {/* Status banner */}
      <Card>
        <Card.Body>
          <div className="flex items-center gap-3">
            <Rocket size={18} className={executionReady ? 'text-v2-success' : 'text-v2-ink-muted'} />
            <div className="flex-1">
              <h3 className="text-[14px] font-bold m-0 text-v2-ink">
                {executionReady ? 'Execution Ready' : 'Not yet Execution Ready'}
              </h3>
              <p className="text-[12px] text-v2-ink-muted m-0 mt-0.5">
                {executionReady
                  ? 'Readiness attestation sealed — the gateway admits governed production traffic.'
                  : 'Governed production traffic is fail-closed until this agent is marked Execution Ready.'}
              </p>
            </div>
            <Pill tone={executionReady ? 'success' : 'muted'} size="sm">
              {executionReady ? 'Sealed' : 'Pending'}
            </Pill>
          </div>
        </Card.Body>
      </Card>

      {/* Execution-Ready gate (displayed == enforced) + Mark Execution Ready CTA */}
      <PhaseGate
        phase="ACT"
        title={`Execution-Ready gate · ${passedCount} of ${criteria.length} passing`}
        criteria={criteria}
        advanceAction={
          <div className="flex items-center gap-2">
            <span className="text-[11px] text-v2-ink-muted tabular-nums">
              Gate: {passedCount} of {criteria.length}
            </span>
            <Button
              variant="primary"
              size="sm"
              loading={marking}
              disabled={executionReady || !allGreen || !registrationId || marking}
              onClick={markReady}
              title={executionReady
                ? 'Already Execution Ready'
                : allGreen
                  ? 'Seal the readiness attestation and admit production traffic'
                  : 'All checks must pass before marking Execution Ready'}>
              {executionReady ? 'Execution Ready' : 'Mark Execution Ready'}
            </Button>
          </div>
        }
      />

      {/* Bug D1/D2 — the Decide-phase settable attributes shown READ-ONLY in Act
          so it's unambiguous what is being sealed. Edit via Promote → Decide. */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <h3 className="text-[14px] font-bold tracking-tight m-0">What you're marking ready</h3>
          <span className="text-[11px] text-v2-ink-muted">read-only · edit in Promote → Decide</span>
        </div>
        <div className="p-4 grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))' }}>
          <div className="rounded-v2-sm bg-v2-muted px-3 py-2">
            <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">Autonomy ceiling</div>
            <div className="text-[13px] font-semibold text-v2-ink mt-0.5">{autonomyLabel(autonomyLevel)}</div>
          </div>
          <div className="rounded-v2-sm bg-v2-muted px-3 py-2">
            <div className="text-[10.5px] uppercase tracking-wider text-v2-ink-muted">Trust baseline</div>
            <div className="text-[13px] font-semibold text-v2-ink mt-0.5">{baselineReg ? 'Registered' : 'Not registered'}</div>
          </div>
        </div>
      </Card>

      {/* Policy skills — shared <SkillPromotionCard> (identical card across all
          agentlet types). Act-phase skills render locked with Regress / Re-push;
          anything not yet in Act surfaces a promote-through-Build→Decide advisory. */}
      {(() => {
        const liveSkills = actSkills.filter((s) => (s.skillPhase || 'build').toLowerCase() === 'act');
        const pending = actSkills.filter((s) => (s.skillPhase || 'build').toLowerCase() !== 'act');
        return (
          <Card padding="none">
            <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
              <h3 className="text-[14px] font-bold tracking-tight m-0">Policy skills in this release</h3>
              <span className="text-[11px] text-v2-ink-muted">{liveSkills.length} live · {pending.length} pending</span>
            </div>
            <div className="p-4 space-y-3">
              {actSkills.length === 0 ? (
                <p className="text-[12px] text-v2-ink-muted m-0">No policy skills on this agent yet.</p>
              ) : (
                <>
                  {liveSkills.length > 0 && (
                    <div className="grid gap-3" style={{ gridTemplateColumns: 'repeat(auto-fill, minmax(360px, 1fr))' }}>
                      {liveSkills.map((s) => (
                        <SkillPromotionCard
                          key={s.id}
                          agentId={agentId}
                          skill={s}
                          surface="act"
                          onChanged={loadExecConfig}
                          renderEval={(sk) => <SkillEvalPanel agentId={agentId} skill={sk} onChanged={loadExecConfig} />}
                        />
                      ))}
                    </div>
                  )}
                  {pending.length > 0 && (
                    <div className="rounded-v2-md px-3 py-2 text-[12px] flex items-center gap-2"
                      style={{ background: 'var(--v2-warning-soft)', color: 'var(--v2-warning)' }}>
                      <span>
                        {pending.length} skill{pending.length > 1 ? 's' : ''} not yet in Act — advance through
                        <strong> Build</strong> → <strong>Decide</strong> to activate.
                      </span>
                    </div>
                  )}
                </>
              )}
            </div>
          </Card>
        );
      })()}

      {/* Live probe */}
      <Card padding="none">
        <div className="flex items-center gap-3 px-4 pt-4 pb-3 border-b border-v2-border-subtle">
          <PlayCircle size={16} className="text-v2-cyan" />
          <h3 className="text-[14px] font-bold tracking-tight m-0">Live probe</h3>
          <span className="text-[11px] text-v2-ink-muted">{probeResults.length} on record</span>
          <div className="ml-auto">
            <Button variant="secondary" size="sm" loading={probing} onClick={runProbe} disabled={!registrationId || probing}>
              Run live probe
            </Button>
          </div>
        </div>
        <div className="p-4">
          {probeResults.length === 0 ? (
            <p className="text-[12px] text-v2-ink-muted m-0">
              No probes yet. Run a live probe to record a result — at least one PASSED probe is required.
            </p>
          ) : (
            <ul className="space-y-1.5 list-none p-0 m-0">
              {probeResults.slice(-6).reverse().map((t, i) => (
                <li key={t.id || i} className="flex items-center gap-3 text-[12px] rounded-v2-sm bg-v2-muted px-3 py-2">
                  <Pill tone={String(t.status).toUpperCase() === 'PASSED' ? 'success' : 'danger'} size="sm">
                    {t.status}
                  </Pill>
                  <span className="flex-1 min-w-0 truncate text-v2-ink">{t.testName || t.source || 'Probe'}</span>
                  <span className="text-[10.5px] text-v2-ink-muted">{t.source || ''}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </Card>

    </div>
  );
}
